import type { SpotlightBounds } from './utils'
import { resolveGuideTourTargetRect } from './utils'
import {
  getGuideTourLockedScrollY,
  isGuideTourPageScrollLocked,
  setGuideTourLockedScrollY,
} from './scrollLock'

export const GUIDE_TOUR_TARGET_SCROLL_BEHAVIOR: ScrollBehavior = 'smooth'

export const GUIDE_TOUR_TARGET_VIEWPORT_MARGIN = 24

export {
  GUIDE_TOUR_POST_SCROLL_REVEAL_MS,
  GUIDE_TOUR_SMOOTH_SCROLL_FALLBACK_MS,
} from './overlayTiming'

/** Interval while smooth-scrolling to a target so visibility is re-checked even without scroll events. */
export const GUIDE_TOUR_SCROLL_POLL_INTERVAL_MS = 50

/** How long the target position must stay unchanged before revealing the spotlight. */
export const GUIDE_TOUR_LAYOUT_STABILITY_MS = 300

export const GUIDE_TOUR_LAYOUT_STABILITY_POLL_MS = 50

/** Never block reveal longer than this while waiting for layout to settle. */
export const GUIDE_TOUR_LAYOUT_STABILITY_MAX_MS = 2_500

/** Debounce before re-scrolling when layout shifts after the spotlight was shown. */
export const GUIDE_TOUR_LAYOUT_RECOVERY_DEBOUNCE_MS = 80

export type ViewportSize = {
  width: number
  height: number
}

export type GuideTourTargetPosition = {
  top: number
  left: number
}

export const getGuideTourTargetPosition = (
  element: HTMLElement,
  getRect: (target: HTMLElement) => DOMRect = resolveGuideTourTargetRect
): GuideTourTargetPosition => {
  const rect = getRect(element)

  return {
    top: rect.top,
    left: rect.left,
  }
}

export const isGuideTourTargetPositionStable = (
  previous: GuideTourTargetPosition,
  next: GuideTourTargetPosition
): boolean => previous.top === next.top && previous.left === next.left

export const waitForGuideTourTargetPositionStability = (
  element: HTMLElement,
  {
    stableMs = GUIDE_TOUR_LAYOUT_STABILITY_MS,
    pollMs = GUIDE_TOUR_LAYOUT_STABILITY_POLL_MS,
    maxMs = GUIDE_TOUR_LAYOUT_STABILITY_MAX_MS,
    getPosition = getGuideTourTargetPosition,
    now = () => Date.now(),
  }: {
    stableMs?: number
    pollMs?: number
    maxMs?: number
    getPosition?: (target: HTMLElement) => GuideTourTargetPosition
    now?: () => number
  } = {}
): Promise<void> =>
  new Promise(resolve => {
    let lastPosition = getPosition(element)
    let stableSince = now()
    const startedAt = stableSince

    const finish = () => {
      window.clearInterval(pollIntervalId)
      window.clearTimeout(maxTimeoutId)
      resolve()
    }

    const pollIntervalId = window.setInterval(() => {
      const nextPosition = getPosition(element)

      if (!isGuideTourTargetPositionStable(lastPosition, nextPosition)) {
        lastPosition = nextPosition
        stableSince = now()
        return
      }

      if (now() - stableSince >= stableMs || now() - startedAt >= maxMs) {
        finish()
      }
    }, pollMs)

    const maxTimeoutId = window.setTimeout(finish, maxMs)
  })

export const isGuideTourTargetVisibleInViewport = (
  bounds: SpotlightBounds,
  viewport: ViewportSize,
  margin = GUIDE_TOUR_TARGET_VIEWPORT_MARGIN
): boolean => {
  const viewportBottom = viewport.height - margin
  const viewportRight = viewport.width - margin
  const targetBottom = bounds.top + bounds.height
  const targetRight = bounds.left + bounds.width

  const intersectsVertically =
    targetBottom > margin && bounds.top < viewportBottom
  const intersectsHorizontally =
    targetRight > margin && bounds.left < viewportRight

  if (!intersectsVertically || !intersectsHorizontally) {
    return false
  }

  const visibleTop = Math.max(bounds.top, margin)
  const visibleBottom = Math.min(targetBottom, viewportBottom)
  const visibleHeight = Math.max(0, visibleBottom - visibleTop)
  const availableHeight = Math.max(0, viewport.height - margin * 2)

  if (bounds.height <= availableHeight) {
    return bounds.top >= margin && targetBottom <= viewportBottom
  }

  const minimumVisibleHeight = Math.min(bounds.height, availableHeight) * 0.4

  return visibleHeight >= minimumVisibleHeight
}

const shouldScrollWindowForGuideTourTarget = (element: HTMLElement): boolean =>
  element.tagName.includes('-')

const getGuideTourCenteredScrollTop = (
  element: HTMLElement,
  viewportHeight = window.innerHeight
): number => {
  const rect = resolveGuideTourTargetRect(element)
  const currentScrollY = getGuideTourLockedScrollY()

  return (
    currentScrollY +
    rect.top -
    Math.max(0, viewportHeight / 2 - rect.height / 2)
  )
}

const applyGuideTourPageScroll = (
  top: number,
  behavior: ScrollBehavior = GUIDE_TOUR_TARGET_SCROLL_BEHAVIOR
): void => {
  const targetScrollY = Math.max(0, top)

  if (isGuideTourPageScrollLocked()) {
    setGuideTourLockedScrollY(targetScrollY)
    return
  }

  window.scrollTo({
    top: targetScrollY,
    left: 0,
    behavior,
  })
}

export const scrollGuideTourTargetIntoView = (
  element: HTMLElement,
  behavior: ScrollBehavior = GUIDE_TOUR_TARGET_SCROLL_BEHAVIOR,
  { forcePageScroll = false }: { forcePageScroll?: boolean } = {}
): void => {
  const rect = resolveGuideTourTargetRect(element)

  if (rect.width <= 0 && rect.height <= 0) {
    return
  }

  if (
    forcePageScroll ||
    shouldScrollWindowForGuideTourTarget(element) ||
    isGuideTourPageScrollLocked()
  ) {
    applyGuideTourPageScroll(getGuideTourCenteredScrollTop(element), behavior)
    return
  }

  if (typeof element.scrollIntoView === 'function') {
    element.scrollIntoView({
      behavior,
      block: 'center',
      inline: 'nearest',
    })
  }
}

/** Immediately recenters the target, including while the tour keeps the page scroll locked. */
export const syncGuideTourTargetInView = (
  element: HTMLElement,
  options: { forcePageScroll?: boolean } = {}
): void => {
  scrollGuideTourTargetIntoView(element, 'auto', options)
}

export const hasGuideTourTargetPositionDrifted = (
  previous: SpotlightBounds | null,
  next: SpotlightBounds,
  threshold = 8
): boolean => {
  if (!previous) {
    return false
  }

  return (
    Math.abs(previous.top - next.top) > threshold ||
    Math.abs(previous.left - next.left) > threshold
  )
}

export { waitForGuideTourScrollSettle } from './waitForGuideTourScrollSettle'
